/**
 * Step labels (D13, docs/platform-agent/contracts.md §5): the short working line the panel shows for the step Amigo
 * is taking, in the person's language ("Checking the menu…"). A port of the agent's `agent/lib/step-labels.ts`
 * (A22) with the same table, so the panel and the agent's request log (§7) read alike; change both with §5.
 * Pure: no React, no i18n.
 */

/** The languages the labels come in; any other falls back to English. */
export const STEP_LABEL_LANGUAGES = Object.freeze(['en', 'de']);

const label = (en, de) => Object.freeze({ en, de });

/**
 * The labels of §5 by tool name, each tool's by area id; `*` is the label without an area (or for an area the table
 * doesn't name). null: the step isn't shown. Every label ends with "…".
 */
export const STEP_LABELS = Object.freeze({
  foodamigos__areas: {
    '*': label('Checking what I can help with here…', 'Ich schaue, wobei ich hier helfen kann…'),
  },
  foodamigos__area_describe: {
    menu: label('Looking at the menu…', 'Ich schaue mir die Speisekarte an…'),
    hours: label('Looking at the opening hours…', 'Ich schaue mir die Öffnungszeiten an…'),
    order_settings: label('Looking at the order settings…', 'Ich schaue mir die Bestelleinstellungen an…'),
    my_info: label('Looking at your details…', 'Ich schaue mir deine Angaben an…'),
    marketing: label('Looking at your marketing…', 'Ich schaue mir dein Marketing an…'),
    gift_cards: label('Looking at the gift cards…', 'Ich schaue mir die Geschenkkarten an…'),
    reports: label('Looking at the reports…', 'Ich schaue mir die Berichte an…'),
    '*': label('Looking at what I can do here…', 'Ich schaue, was ich hier tun kann…'),
  },
  foodamigos__read: {
    menu: label('Checking the menu…', 'Ich prüfe die Speisekarte…'),
    hours: label('Checking the opening hours…', 'Ich prüfe die Öffnungszeiten…'),
    order_settings: label('Checking the order settings…', 'Ich prüfe die Bestelleinstellungen…'),
    my_info: label('Checking your details…', 'Ich prüfe deine Angaben…'),
    marketing: label('Checking your marketing…', 'Ich prüfe dein Marketing…'),
    gift_cards: label('Checking the gift cards…', 'Ich prüfe die Geschenkkarten…'),
    reports: label('Checking your numbers…', 'Ich prüfe deine Zahlen…'),
    '*': label('Reading your data…', 'Ich lese deine Daten…'),
  },
  foodamigos__propose: {
    menu: label('Preparing the menu change…', 'Ich bereite die Änderung der Speisekarte vor…'),
    hours: label('Preparing the opening hours change…', 'Ich bereite die Änderung der Öffnungszeiten vor…'),
    order_settings: label(
      'Preparing the order settings change…',
      'Ich bereite die Änderung der Bestelleinstellungen vor…',
    ),
    my_info: label('Preparing the change to your details…', 'Ich bereite die Änderung deiner Angaben vor…'),
    marketing: label('Preparing the marketing change…', 'Ich bereite die Marketing-Änderung vor…'),
    gift_cards: label('Preparing the gift card change…', 'Ich bereite die Änderung der Geschenkkarten vor…'),
    '*': label('Preparing the change…', 'Ich bereite die Änderung vor…'),
  },
  foodamigos__change_set_get: {
    '*': label('Checking the change…', 'Ich prüfe die Änderung…'),
  },
  foodamigos__gap_record: {
    '*': label('Noting this for our team…', 'Ich notiere das für unser Team…'),
  },
  foodamigos__request_log: null,
  research: {
    '*': label('Looking up how this works…', 'Ich schaue nach, wie das funktioniert…'),
  },
  screen_find: { '*': label('Finding the right page…', 'Ich suche die richtige Seite…') },
  file_read: { '*': label('Reading your file…', 'Ich lese deine Datei…') },
  shop_check: { '*': label('Checking your shop…', 'Ich prüfe deinen Shop…') },
  show_card: null,
  refusal_note: null,
  // Bookkeeping the person has no use for (QA75): a question shows as its own card, and eve's tool search finds a
  // tool before Amigo calls it.
  ask_question: null,
  eve__search: null,
  eve__tool: null,
  eve__skill: null,
});

/**
 * What a step that failed reads as (QA75, QL46, §5 Failed steps): what couldn't be done, in plain words and without
 * "…", for the area it worked in, so the panel never shows a vague step in red. null: a failed step isn't shown
 * (bookkeeping, and steps whose failure the answer itself explains).
 */
export const STEP_FAILED_LABELS = Object.freeze({
  foodamigos__areas: null,
  foodamigos__area_describe: null,
  foodamigos__read: {
    menu: label("Couldn't read the menu", 'Ich konnte die Speisekarte nicht lesen'),
    hours: label("Couldn't read the opening hours", 'Ich konnte die Öffnungszeiten nicht lesen'),
    order_settings: label("Couldn't read the order settings", 'Ich konnte die Bestelleinstellungen nicht lesen'),
    my_info: label("Couldn't read your details", 'Ich konnte deine Angaben nicht lesen'),
    marketing: label("Couldn't read your marketing", 'Ich konnte dein Marketing nicht lesen'),
    gift_cards: label("Couldn't read the gift cards", 'Ich konnte die Geschenkkarten nicht lesen'),
    reports: label("Couldn't read your numbers", 'Ich konnte deine Zahlen nicht lesen'),
    '*': label("Couldn't read your data", 'Ich konnte deine Daten nicht lesen'),
  },
  foodamigos__propose: {
    menu: label("Couldn't prepare the menu change", 'Ich konnte die Änderung der Speisekarte nicht vorbereiten'),
    hours: label(
      "Couldn't prepare the opening hours change",
      'Ich konnte die Änderung der Öffnungszeiten nicht vorbereiten',
    ),
    order_settings: label(
      "Couldn't prepare the order settings change",
      'Ich konnte die Änderung der Bestelleinstellungen nicht vorbereiten',
    ),
    my_info: label(
      "Couldn't prepare the change to your details",
      'Ich konnte die Änderung deiner Angaben nicht vorbereiten',
    ),
    marketing: label("Couldn't prepare the marketing change", 'Ich konnte die Marketing-Änderung nicht vorbereiten'),
    gift_cards: label(
      "Couldn't prepare the gift card change",
      'Ich konnte die Änderung der Geschenkkarten nicht vorbereiten',
    ),
    '*': label("Couldn't prepare the change", 'Ich konnte die Änderung nicht vorbereiten'),
  },
  foodamigos__change_set_get: { '*': label("Couldn't check the change", 'Ich konnte die Änderung nicht prüfen') },
  foodamigos__gap_record: null,
  foodamigos__request_log: null,
  research: {
    '*': label("Couldn't look up how this works", 'Ich konnte nicht nachschauen, wie das funktioniert'),
  },
  screen_find: { '*': label("Couldn't find the page", 'Ich konnte die Seite nicht finden') },
  file_read: { '*': label("Couldn't read your file", 'Ich konnte deine Datei nicht lesen') },
  shop_check: { '*': label("Couldn't open your shop", 'Ich konnte deinen Shop nicht öffnen') },
  show_card: null,
  refusal_note: null,
  ask_question: null,
  eve__search: null,
  eve__tool: null,
  eve__skill: null,
});

/** The label language for a language tag ("de", "de-DE", "en-GB"): its own when the table has it, else English. */
export function stepLabelLanguage(language) {
  const code = (language ?? '').trim().toLowerCase().slice(0, 2);

  return STEP_LABEL_LANGUAGES.includes(code) ? code : 'en';
}

function areaIn(input) {
  const area = input && typeof input === 'object' ? input.area : null;

  return typeof area === 'string' && area ? area : null;
}

/**
 * The area a step works in: the call's own `area` (area_describe, gap_record), else that of the turn's latest
 * area_describe before it. A read or a proposal carries no area, but Amigo describes an area before it reads or
 * proposes there.
 */
function areaOf(input, previous) {
  const own = areaIn(input);

  if (own) {
    return own;
  }

  for (let index = previous.length - 1; index >= 0; index -= 1) {
    const call = previous[index];

    if (call?.tool === 'foodamigos__area_describe') {
      const area = areaIn(call.input);

      if (area) {
        return area;
      }
    }
  }

  return null;
}

/**
 * What a step reads as in the panel, in the person's language (§5): the label of `toolName` for the area it works in,
 * or the tool's label without an area. `previous` are the turn's earlier calls (`[{ tool, input }]`), in order. null
 * when the step isn't shown (show_card, request_log) or the table doesn't name the tool (loading a skill,
 * ask_question).
 */
export function stepLabel(toolName, input, language, previous = []) {
  const labels = Object.hasOwn(STEP_LABELS, toolName) ? STEP_LABELS[toolName] : null;

  if (!labels) {
    return null;
  }

  const area = areaOf(input, previous ?? []);
  const text = (area !== null && Object.hasOwn(labels, area) ? labels[area] : null) ?? labels['*'];

  return text[stepLabelLanguage(language)];
}

/**
 * Which of a failed step's labels applies (QL46): the area it worked in (found like `stepLabel`'s) when the failed
 * labels of `toolName` name that area, else `*`. null when a failed step isn't shown; undefined for a tool the table
 * doesn't know (a tool added to the agent before the panel), which keeps the step's own label. The panel's step list
 * reads the label by this area from its catalog (`failed_steps`), so it comes in every language the dashboard has.
 */
export function stepFailedArea(toolName, input, previous = []) {
  if (!Object.hasOwn(STEP_FAILED_LABELS, toolName)) {
    return undefined;
  }

  const labels = STEP_FAILED_LABELS[toolName];

  if (!labels) {
    return null;
  }

  const area = areaOf(input, previous ?? []);

  return area !== null && Object.hasOwn(labels, area) ? area : '*';
}

/**
 * What a failed step reads as, in the person's language (§5 Failed steps, as the agent's request log reads it): the
 * failed label of `toolName` for the area it worked in, or the tool's without an area. null when a failed step isn't
 * shown; undefined for a tool the table doesn't know.
 */
export function stepFailedLabel(toolName, input, language, previous = []) {
  const area = stepFailedArea(toolName, input, previous);

  if (area === null || area === undefined) {
    return area;
  }

  return STEP_FAILED_LABELS[toolName][area][stepLabelLanguage(language)];
}
